import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/sr';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import ShiftService from '../services/ShiftService';
import UserService from '../services/UserService';
import AuthService from '../services/AuthService';

moment.locale('sr');
const localizer = momentLocalizer(moment);

const CompactEvent = ({ event }) => (
  <div style={{ padding: '2px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
    <span style={{ fontWeight: '600' }}>{event.userNameOnly}</span>
    <span style={{ opacity: 0.9, fontSize: '0.85em', marginLeft: '5px' }}>
      {moment(event.start).format('HH:mm')}
    </span>
  </div>
);

const StaffCalendar = () => {
  const [events, setEvents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState({ date: null, shifts: [] });
  
  const [newShift, setNewShift] = useState({ start: '', end: '', userId: '' });

  const currentUser = AuthService.getCurrentUser();
  const canManage = currentUser?.roles?.some(role => 
    ["ROLE_ADMIN", "ROLE_DOCTOR", "ROLE_NURSE"].includes(role)
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [shiftsData, staffData] = await Promise.all([
        ShiftService.getAll(),
        UserService.getAllStaff()
      ]);
      setStaffList(staffData);
      const formatted = shiftsData.map(s => ({
        id: s.id,
        userNameOnly: s.title ? s.title.split('(')[0].trim() : "Osoblje",
        start: new Date(s.start),
        end: new Date(s.end),
        role: s.role,
        userId: s.userId
      }));
      setEvents(formatted);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDayClick = (date) => {
    const dayShifts = events.filter(e => moment(e.start).isSame(date, 'day'));
    setSelectedDayData({ date, shifts: dayShifts });
    setShowDayModal(true);
  };

  const openAddFromDay = () => {
    const startDT = moment(selectedDayData.date).set({ hour: 7, minute: 0 }).format('YYYY-MM-DDTHH:mm');
    const endDT = moment(selectedDayData.date).set({ hour: 15, minute: 0 }).format('YYYY-MM-DDTHH:mm');
    setNewShift({ start: startDT, end: endDT, userId: '' });
    setShowDayModal(false);
    setShowAddModal(true);
  };

  const handleSelectEvent = async (event) => {
    if (!canManage) return;
    if (window.confirm(`Obriši smenu za: ${event.userNameOnly}?`)) {
      try {
        await ShiftService.delete(event.id);
        setShowDayModal(false);
        fetchData();
      } catch (err) { alert(err.message); }
    }
  };

  return (
    <div style={{ height: '92vh', padding: '30px', backgroundColor: '#f4f7f9' }}>
      <style>{`
        .rbc-calendar { font-family: 'Inter', sans-serif; background: white; border-radius: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.05); padding: 15px; border: none; }
        .rbc-header { padding: 12px !important; font-weight: 700 !important; color: #64748b !important; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 0.05em; border-bottom: 2px solid #f1f5f9 !important; }
        .rbc-day-bg { transition: background 0.2s; border-left: 1px solid #f1f5f9 !important; }
        .rbc-day-bg:hover { background-color: #f8fafc !important; cursor: pointer; }
        .rbc-today { background-color: #eff6ff !important; }
        .rbc-event { transition: transform 0.1s; border: none !important; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
        .rbc-event:hover { transform: translateY(-1px); }
        .rbc-off-range-bg { background-color: #fafafa; }
        .list-group-item { border: none !important; margin-bottom: 8px; border-radius: 12px !important; background: #f8fafc !important; transition: all 0.2s; }
        .list-group-item:hover { background: #f1f5f9 !important; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '25px', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontWeight: '800', letterSpacing: '-0.02em' }}>Raspored smena</h2>
          <p style={{ margin: 0, color: '#64748b' }}>Upravljajte radnim vremenom vašeg tima</p>
        </div>
        <button onClick={fetchData} className="btn" style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', color: '#475569', fontWeight: '600', padding: '10px 20px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
          🔄 Osveži prikaz
        </button>
      </div>

      <div style={{ height: 'calc(100% - 80px)' }}>
        <Calendar
          localizer={localizer}
          events={events}
          defaultView="month"
          views={['month']}
          onSelectSlot={({ start }) => handleDayClick(start)}
          onDrillDown={(date) => handleDayClick(date)}
          onSelectEvent={handleSelectEvent}
          selectable
          components={{ event: CompactEvent }}
          eventPropGetter={(e) => ({
            style: { 
              backgroundColor: e.role?.includes("DOCTOR") ? '#3b82f6' : '#10b981', 
              borderRadius: '8px', 
              fontSize: '11px',
              border: 'none',
              padding: '2px 5px'
            }
          })}
        />
      </div>

      {/* MODAL ZA PREGLED DANA */}
      {showDayModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, width: '450px' }}>
            <div style={{ borderBottom: '2px solid #f1f5f9', marginBottom: '20px', paddingBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>
                📅 {moment(selectedDayData.date).format('DD. MMMM YYYY.')}
              </h5>
              <button onClick={() => setShowDayModal(false)} style={{ border: 'none', background: '#f1f5f9', width: '32px', height: '32px', borderRadius: '50%', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>&times;</button>
            </div>
            
            <div style={{ maxHeight: '350px', overflowY: 'auto', marginBottom: '20px', paddingRight: '5px' }}>
              {selectedDayData.shifts.length > 0 ? (
                <ul className="list-group">
                  {selectedDayData.shifts.map(s => (
                    <li key={s.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: s.role?.includes("DOCTOR") ? '#3b82f6' : '#10b981' }}></div>
                        <div>
                          <div style={{ fontWeight: '600', color: '#334155' }}>{s.userNameOnly}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>{moment(s.start).format('HH:mm')} - {moment(s.end).format('HH:mm')}</div>
                        </div>
                      </div>
                      {canManage && <button className="btn btn-sm" onClick={() => handleSelectEvent(s)} style={{ color: '#ef4444', background: '#fee2e2', border: 'none', borderRadius: '8px', padding: '5px 10px', fontSize: '12px', fontWeight: '600' }}>Briši</button>}
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                  <div style={{ fontSize: '30px', marginBottom: '10px' }}>⏳</div>
                  <p>Nema zakazanih smena za ovaj datum.</p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              {canManage && <button className="btn btn-primary w-100" onClick={openAddFromDay} style={{ borderRadius: '12px', padding: '12px', fontWeight: '600', border: 'none', background: '#3b82f6', boxShadow: '0 4px 6px rgba(59, 130, 246, 0.2)' }}>+ Nova smena</button>}
              <button className="btn btn-light w-100" onClick={() => setShowDayModal(false)} style={{ borderRadius: '12px', padding: '12px', fontWeight: '600', background: '#f1f5f9', border: 'none', color: '#475569' }}>Zatvori</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ZA DODAVANJE SMENE */}
      {showAddModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, width: '400px' }}>
            <h4 style={{ marginBottom: '25px', fontWeight: '800', color: '#1e293b', letterSpacing: '-0.02em' }}>Nova smena</h4>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await ShiftService.create({ 
                  startTime: newShift.start, 
                  endTime: newShift.end, 
                  user: { userID: parseInt(newShift.userId) } 
                });
                setShowAddModal(false);
                fetchData();
              } catch (err) { alert(err.message); }
            }}>
              <div className="mb-4">
                <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontWeight: '600', fontSize: '14px' }}>Zaposleni</label>
                <select className="form-select" value={newShift.userId} onChange={e => setNewShift({...newShift, userId: e.target.value})} required style={{ borderRadius: '12px', padding: '12px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                  <option value="">Izaberi člana tima...</option>
                  {staffList.map(u => (
                    <option key={u.userID} value={u.userID}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="row mb-4">
                <div className="col">
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontWeight: '600', fontSize: '14px' }}>Početak</label>
                  <input type="datetime-local" className="form-control" value={newShift.start} onChange={e => setNewShift({...newShift, start: e.target.value})} required style={{ borderRadius: '12px', padding: '12px', border: '1px solid #e2e8f0' }} />
                </div>
              </div>
              <div className="row mb-4">
                <div className="col">
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontWeight: '600', fontSize: '14px' }}>Kraj</label>
                  <input type="datetime-local" className="form-control" value={newShift.end} onChange={e => setNewShift({...newShift, end: e.target.value})} required style={{ borderRadius: '12px', padding: '12px', border: '1px solid #e2e8f0' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                <button type="submit" className="btn btn-success w-100" style={{ borderRadius: '12px', padding: '12px', fontWeight: '600', background: '#10b981', border: 'none', boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)' }}>Sačuvaj</button>
                <button type="button" className="btn btn-light w-100" onClick={() => setShowAddModal(false)} style={{ borderRadius: '12px', padding: '12px', fontWeight: '600', background: '#f1f5f9', border: 'none' }}>Odustani</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContentStyle = { backgroundColor: 'white', padding: '30px', borderRadius: '24px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)' };

export default StaffCalendar;